
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { FaCheck, FaSearchMinus, FaSearchPlus, FaCrop } from "react-icons/fa";

/**
 * 🔧 Crop configuration by type
 * Add new types here – NO component changes needed
 */
const CROP_CONFIG = {

  "product-image": {
    title: "Product Image",
    size: window.imageConfig?.PRODUCT_IMAGE_SIZE || { width: 600, height: 600 },
    quality: 0.95,
  },

};

const ImageCropperModal = ({
  show,
  imageUrl,
  type = "category-image",
  onClose,
  onCropped,
}) => {
  const cropperRef = useRef(null);

  // 🔹 Pick config based on type
  const cropConfig = CROP_CONFIG[type] || CROP_CONFIG["category-image"];
  const targetSize = cropConfig.size;
  const aspectRatio = targetSize.width / targetSize.height;

  // Crop states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Reset when modal opens or image/type changes
  useEffect(() => {
    if (show) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [show, imageUrl, type]);

  // Store cropped area in pixels
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Convert cropped area to resized image blob
  const getCroppedImageBlob = async () => {
    const image = await createImage(imageUrl);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = targetSize.width;
    canvas.height = targetSize.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      targetSize.width,
      targetSize.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", cropConfig.quality);
    });
  };

  // Apply crop
  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;

    const blob = await getCroppedImageBlob();
    const file = new File([blob], `cropped-${type}-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });

    onCropped(file);
    onClose();
  };

  // Utility: create image object
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex justify-content-between align-items-center w-100">
          <span>
            <FaCrop className="me-2" />
            Crop <strong>{cropConfig.title}</strong>
          </span>

          <div className="d-flex align-items-center gap-3 px-4">
            <span>{Math.round(zoom * 100)}%</span>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
            >
              <FaSearchMinus />
            </Button>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            >
              <FaSearchPlus />
            </Button>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {imageUrl && (
          <div style={{ position: "relative", width: "100%", height: "70vh" }}>
            <Cropper
              ref={cropperRef}
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              cropShape="rect"
              showGrid={false}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="primary"
          disabled={!croppedAreaPixels}
          onClick={handleApplyCrop}
        >
          <FaCheck className="me-2" /> Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropperModal;
